import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  Button,
  IconButton,
  Input,
  Text,
  XStack,
  YStack,
} from '@/components/ui';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';

type ShareInvitePeopleProps = {
  idPrefix: string;
  invitedEmails: string[];
  onAdd: (draft: string) => string | null;
  onRemove: (email: string) => void;
};

export const ShareInvitePeople = ({
  idPrefix,
  invitedEmails,
  onAdd,
  onRemove,
}: ShareInvitePeopleProps) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const hintId = `${idPrefix}-invite-hint`;

  const submit = () => {
    if (!draft.trim()) return;
    const inviteError = onAdd(draft);
    setError(inviteError);
    if (!inviteError) setDraft('');
  };

  return (
    <YStack className="-mx-4 gap-2 bg-surface-active px-4 pb-2.5">
      <YStack className="gap-1">
        <XStack className="gap-1.5 border border-border-strong rounded-md p-1.5 w-full items-center bg-surface focus-within:border-accent">
          <Input
            id={`${idPrefix}-invite-email`}
            type="email"
            variant="plain"
            inputSize="sm"
            className="h-6 px-1.5 shadow-none"
            placeholder="Add people by email"
            aria-label="Add people by email"
            aria-invalid={!!error}
            aria-describedby={hintId}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              submit();
            }}
          />
          <Button
            size="xs"
            className="shrink-0"
            disabled={!draft.trim()}
            onClick={submit}
          >
            Invite
          </Button>
        </XStack>
        <Text
          id={hintId}
          size="xs"
          variant={error ? 'default' : 'muted'}
          className={cn(error && 'text-danger-11')}
          aria-live="polite"
        >
          {error ?? "They'll get an email with a link to open it"}
        </Text>
      </YStack>
      {invitedEmails.length > 0 && (
        <YStack className="gap-1" data-testid={`${idPrefix}-invited`}>
          {invitedEmails.map((email) => (
            <XStack key={email} className="items-center gap-2">
              <InitialsAvatar
                name={email}
                seed={email}
                className="ph-no-capture size-6"
              />
              <Text size="xs" className="flex-1 min-w-0 truncate">
                {email}
              </Text>
              <IconButton
                size="xs"
                icon={Icons.cancel}
                tooltip="Remove access"
                aria-label="Remove access"
                onClick={() => onRemove(email)}
              />
            </XStack>
          ))}
        </YStack>
      )}
    </YStack>
  );
};
